import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionBids } from 'dibs-auctions/exports/hooks';

// components
import { FormattedPrice } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';

// types
import { PdpSharedAuctionActionBidLabel_item$key as Item } from './__generated__/PdpSharedAuctionActionBidLabel_item.graphql';

// styles
import styles from './PdpSharedAuctionActionBidLabel.scss';

type Props = {
    item: Item;
    currency: string;
    isCloseToReserve: boolean;
};

type RelayVariables = {
    userId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const itemFragment = graphql`
    fragment PdpSharedAuctionActionBidLabel_item on Item {
        ...useAuctionBids_item @arguments(fetchUser: $fetchUser)
    }
`;

export const PdpSharedAuctionActionBidLabel: FC<Props> = ({
    item: itemRef,
    currency,
    isCloseToReserve,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { userId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { hasBid, hasHighestBid, hasHighestBidAndReserveMet, highestBidAmount } = useAuctionBids({
        item,
        userId,
        currency,
    });

    if (!userId || !hasBid || !highestBidAmount) {
        return null;
    }

    const price = <FormattedPrice value={highestBidAmount} currency={currency} />;
    let label = null;

    if (hasHighestBidAndReserveMet) {
        label = (
            <FormattedMessage
                id="PdpSharedAuctionActionBidLabel.hasHighestBidAndReserveMet"
                defaultMessage="<success>You are the highest bidder.</success> Your max bid is {price}."
                values={{
                    price,
                    success: chunks => (
                        <span className={styles.success} data-tn="pdp-highest-bidder-message">
                            {chunks}
                        </span>
                    ),
                }}
            />
        );
    } else if (isCloseToReserve) {
        label = (
            <FormattedMessage
                id="PdpSharedAuctionActionBidLabel.closeToReserve"
                defaultMessage="<warning>Your max bid is {price} and you are close to meeting the reserve.</warning>"
                values={{
                    price,
                    warning: chunks => <span className={styles.warning}>{chunks}</span>,
                }}
            />
        );
    } else if (hasHighestBid) {
        label = (
            <FormattedMessage
                id="PdpSharedAuctionActionBidLabel.hasHighestBid"
                defaultMessage="<warning>Your are the highest bidder, but the reserve has not been met.</warning> Your max bid is {price}."
                values={{
                    price,
                    warning: chunks => (
                        <span className={styles.warning} data-tn="pdp-highest-bidder-message">
                            {chunks}
                        </span>
                    ),
                }}
            />
        );
    } else {
        label = (
            <FormattedMessage
                id="PdpSharedAuctionActionBidLabel.hasBid"
                defaultMessage="<warning>You were outbid.</warning> Your max bid is {price}."
                values={{
                    price,
                    warning: chunks => (
                        <span className={styles.warning} data-tn="pdp-outbid-message">
                            {chunks}
                        </span>
                    ),
                }}
            />
        );
    }

    return label ? (
        <div className={styles.container} data-tn="pdp-max-bid-amount">
            {label}
        </div>
    ) : null;
};
